import "./MyDataset.less"
import { inject, observer } from "mobx-react";
import getStoreFromProps from "../../store/getStoreFromProps";
import { Breadcrumb, Descriptions, Input } from "antd";
import { BreadcrumbItemType } from "antd/es/breadcrumb/Breadcrumb";
import { useState, useRef } from "react";
import Images from "../../utils/Images";
import CustomButton from "../CustomButton/CustomButton";
import { DatasetInfo } from "../../store/DatasetStore";
import { DatasetItem } from "../DatasetItem/DatasetItem";
import { ColorImg } from "../ColorImg/ColorImg";
import { OnClickDocument } from "../OnClickDocument/OnClickDocument";
import { DatasetImgItem } from "../DatasetImgItem/DatasetImgItem";
import { DatasetSelect } from "../DatasetSelect/DatasetSelect";
import {ManualTagDialog} from "../ManualTagDialog/ManualTagDialog";
import { CreateNewDatasetDialog } from "../CreateNewDatasetDialog/CreateNewDatasetDialog";
import { needLogin } from "../../utils/decor";
import { selectImg } from "../../utils/misc";
import { msgbox, MSG_TYPE } from "../../utils/Msgbox";
import { CreateNewDatasetDialogStatus } from "../CreateNewDatasetDialog/CreateNewDatasetDialog";

class TopBtnInfo{
    img: string = "";
    title: string = "";
    onClick: () => void;
    isVisible: () => boolean;
    isActive: () => boolean;

    constructor(img: string, title: string, onClick: () => void, isVisible?: () => boolean, isActive?: () => boolean){
        this.img = img;
        this.title = title;
        this.onClick = onClick;
        this.isVisible = isVisible || (() => true);
        this.isActive = isActive || (() => true);
    }
}

const separator = <span className="my-dataset-path-separator">&gt;</span>;

const CLIP: string = "clip";
const SIZE: string = "size";
const PATH_ROOT: string = "我的数据集";
const PATH_CLIP: string = "图片打标/裁剪";
const clipTexts: string[] = ["中央裁剪", "无需裁剪"];
const sizeTexts: string[] = ["768*768", "768*1024", "1024*768", "1024*1024"];
const MAX_IMG_SIZE: number = 20 * 1024 * 1024;

export const MyDataset = inject("common", "dataset", "advanceSetting")(observer((props: any): React.ReactElement => {
    const commonStore = getStoreFromProps(props, "common");
    const datasetStore = getStoreFromProps(props, "dataset");
    const advanceSettingStore = getStoreFromProps(props, "advanceSetting");
    const selfRef = useRef<HTMLDivElement>(null);
    const [curSelect, setCurSelect] = useState(""); // 当前选中的底部选择框
    const [clipValue, setClipValue] = useState(0);
    const [sizeValue, setSizeValue] = useState(0);
    const [modelPromptValue, setModelPromptValue] = useState("");
    const [isBatch, setIsBatch] = useState(false); // 是否批处理模式
    const [batchId, setBatchedId] = useState(new Set<string>());
    const [isManualTagDialogVisible, setIsManualTagDialogVisible] = useState(false);
    const manualTagDiagStartIndex = useRef<number>(0);
    const [progress, setProgress] = useState(-1); // 预处理进度
    const [isSelectAllClicked, setIsSelectAllClicked] = useState(false);
    const [createNewDatasetDialogState, setCreateNewDatasetDialogState] = useState<CreateNewDatasetDialogStatus>(new CreateNewDatasetDialogStatus(false, "", "", "", () => {})); // [visible, title, description, onConfirm]
    const curEditDatasetIndex = useRef<number>(0);

    const onBatchClick = (): void => {
        setIsBatch(true);
        setBatchedId(new Set<string>([]));
        setIsSelectAllClicked(false);
    };

    const uploadImg = (): void => {
        selectImg().then(files => {
            if(files.length <= 0){
                return;
            }

            setProgress(0);
            datasetStore.uploadImgs(files.filter(item => item.size < MAX_IMG_SIZE), setProgress)
                .catch(reason => msgbox(reason.message, MSG_TYPE.ERROR))
                .finally(() => setProgress(-1));
        });
    };

    const onReUploadClick = (): void => {
        msgbox("确认删除之前上传的图片吗", MSG_TYPE.INFO, uploadImg);
    };

    const onAddImgClick = (): void => {
        selectImg().then(files => {
            if(files.length <= 0){
                return;
            }

            setProgress(0);
            datasetStore.addImgs(files.filter(item => item.size < MAX_IMG_SIZE), setProgress)
                .catch(reason => msgbox(reason.message, MSG_TYPE.ERROR))
                .finally(() => setProgress(-1));
        });
    };

    const onExitBatchClick = (): void => {
        setIsBatch(false);
        setBatchedId(new Set<string>());
    };

    const topBtnInfo: TopBtnInfo[] = [
        new TopBtnInfo(Images.exitBatch, "退出编辑", onExitBatchClick, () => isBatch),
        new TopBtnInfo(Images.edit, "批量编辑", onBatchClick, () => !isBatch),
        new TopBtnInfo(Images.upload, "重新上传", onReUploadClick, undefined, () => !isBatch),
        new TopBtnInfo(Images.add, "添加图片", onAddImgClick, undefined, () => !isBatch)
    ];

    const [pathState, setPathState] = useState<BreadcrumbItemType[]>([
        {title: PATH_ROOT}
    ]);

    const onPathRouteClick = (a: string): void => {
        if(a === PATH_ROOT){
            datasetStore.setCurDataset(null);
            setPathState([{title: PATH_ROOT}]);
            setIsBatch(false);
        }
    };

    const itemRender = (route: BreadcrumbItemType, params: any, routes: BreadcrumbItemType[], paths: string[]): React.ReactNode => {
        return <span className="my-dataset-path-span" data-select={route.title === routes[routes.length - 1].title} data-hoverable={route.title === PATH_ROOT && routes.length > 1}
            onClick={() => onPathRouteClick(route.title as string)}>{route.title}</span>;
    };

    const onCreateDatasetClick = needLogin((): void => {
        setCreateNewDatasetDialogState(new CreateNewDatasetDialogStatus(true, "新建数据集", "", "", onCreateDatasetConfirm));
    });

    const onCreateDatasteClose = (): void => {
        setCreateNewDatasetDialogState(new CreateNewDatasetDialogStatus(false, "", "", "", () => {}));
    };

    const goToDataset = (a: DatasetInfo): void => {
        datasetStore.setCurDataset(a);
        advanceSettingStore.modelName = a.title;
        setPathState([
            {title: PATH_ROOT},
            {title: a.title},
            {title: PATH_CLIP}
        ]);
    };

    const onCreateDatasetConfirm = (title: string, description: string): void => {
        if(title.length <= 0){
            msgbox("数据集名称不能为空", MSG_TYPE.ERROR);
            return;
        }

        setCreateNewDatasetDialogState(new CreateNewDatasetDialogStatus(false, "", "", "", () => {}));
        commonStore.runWithLoadingAnim(datasetStore.createDataset(title, description).then(goToDataset));
    };

    // 空数据集合
    const empty = (): React.ReactElement => {
        return <div className="my-dataset-empty">
            <CustomButton className="my-dataset-empty-new-btn" onClick={onCreateDatasetClick}>
                <>
                    <img src={Images.newDataset}></img>
                    <span>新建数据集</span>
                </>
            </CustomButton>
            <span>最多添加100张图片，支持png/jpg/jpeg格式图像</span>
        </div>;
    };

    // 新数据集
    const uploadImgHint = (): React.ReactElement => {
        return <div className="my-dataset-upload-img-hint">
            <img src={Images.emptyDataset}></img>
            <span onClick={uploadImg}>点击上传图片</span>
            <span>{"最多添加100张图片，支持png/jpg/jpeg\n单张图片不超过20M/张"}</span>
        </div>;
    };

    const onDatasetImgManageClick = (a: DatasetInfo, i: number): void => {
        commonStore.runWithLoadingAnim(datasetStore.getDatasetImgs(a.id).then(() => goToDataset(a)))
    };

    const onDatasetEditConfirm = (title: string, description: string): void => {
        if(title.length <= 0){
            msgbox("数据集名称不能为空", MSG_TYPE.ERROR);
            return;
        }
        setCreateNewDatasetDialogState(new CreateNewDatasetDialogStatus(false, "", "", "", () => {}));
        datasetStore.updateDataset(curEditDatasetIndex.current, title, description);
    };

    const onDatasetEditClick = (a: DatasetInfo, i: number): void => {
        curEditDatasetIndex.current = i;
        setCreateNewDatasetDialogState(new CreateNewDatasetDialogStatus(true, "编辑图片数据库", a.title, a.description, onDatasetEditConfirm));
    };

    const onDatasetDeleteClick = (a: DatasetInfo, i: number): void => {
        msgbox("数据集删除后将不可恢复，是否确认删除?", MSG_TYPE.INFO, () => {
            commonStore.runWithLoadingAnim(datasetStore.deleteDataset(a.id));
        });
    };

    const onDatasetCopyClick = (a: DatasetInfo, i: number): void => {
        commonStore.runWithLoadingAnim(datasetStore.copyDataset(i));
    };

    const onDatasetDownloadClick = (a: DatasetInfo, i: number): void => {
        commonStore.runWithLoadingAnim(a.download());
    };

    // 显示已有数据集
    const datasets = (): React.ReactElement => {
        return <div className="my-dataset-datasets-wrap">
            <div className="my-dataset-datasets-new-dataset" onClick={onCreateDatasetClick}>
                <ColorImg src={Images.newDataset1}></ColorImg>
                <span>新建数据集</span>
            </div>
            {datasetStore.datasets.map((item, i) => <DatasetItem key={i} info={item} onCopyClick={onDatasetCopyClick} onDeleteClick={onDatasetDeleteClick}
                onEditClick={onDatasetEditClick} onImgManageClick={onDatasetImgManageClick} onDownloadClick={onDatasetDownloadClick} index={i}></DatasetItem>)}
        </div>;
    };

    const onSelectClick = (key: string): void => {
        setCurSelect(key);
    };

    const onClipChange = (a: number): void => {
        setClipValue(a);
        setCurSelect("");
    };

    const onSizeChange = (a: number): void => {
        setSizeValue(a);
        setCurSelect("");
    };

    const modelPrompt = (): React.ReactElement => {
        return <div className="my-dataset-model-prompt">
            <div>模型触发词</div>
            <Input variant="borderless" style={{resize:"none"}} className="my-dataset-model-prompt-input" value={modelPromptValue} onChange={e => setModelPromptValue(e.target.value)}
                placeholder="输入触发词"></Input>
        </div>;
    };

    const onBatch = (id: string, checked: boolean): void => {
        if(checked){
            batchId.add(id);
        } else {
            batchId.delete(id);
        }
        setBatchedId(new Set(batchId));
    };

    const onClipBtnClick = (): void => {
        setProgress(0);

        const [preferWidth, preferHeight] = sizeTexts[sizeValue].split("*").map(Number);
        datasetStore.clipImgs(modelPromptValue, clipValue, preferWidth, preferHeight, setProgress)
            .catch(reason => msgbox(reason.message, MSG_TYPE.ERROR))
            .finally(() => setProgress(-1));
    };

    // 数据集图片底部菜单
    const datasetsImgsBottomBtns = (): React.ReactElement => {
        return <div className="my-dataset-datasets-imgs-bottom-btns-wrap">
            <DatasetSelect title="裁剪方法" texts={clipTexts} values={clipTexts} value={clipValue} onChange={onClipChange} customKey={CLIP} isOptionsVisible={curSelect === CLIP} onClick={onSelectClick}></DatasetSelect>
            <DatasetSelect title="裁剪尺寸" texts={sizeTexts} values={sizeTexts} value={sizeValue} onChange={onSizeChange} customKey={SIZE} isOptionsVisible={curSelect === SIZE} onClick={onSelectClick}></DatasetSelect>
            {modelPrompt()}
            <CustomButton className="my-dataset-clip-btn" onClick={onClipBtnClick}>裁剪/打标</CustomButton>
        </div>;
    };

    const onSelectAllClick = (): void => {
        if(isSelectAllClicked){
            setIsSelectAllClicked(false);
            setBatchedId(new Set<string>([]));
        } else {
            setIsSelectAllClicked(true);
            setBatchedId(new Set<string>(datasetStore.curDatasetInfo!.imgs.map(item => item.id)));
        }
    };

    const onDeleteImgClick = (): void => {
        if(batchId.size <= 0){
            return;
        }
        msgbox(`是否删除已选的${batchId.size}张图片`, MSG_TYPE.INFO, () => {
            commonStore.runWithLoadingAnim(datasetStore.deleteImgs(Array.from(batchId))
                .then(() => setIsBatch(false)));
        });
    };

    // 批处理底部菜单
    const datasetsImgsBottomBtnsBatch = (): React.ReactElement => {
        return <div className="my-dataset-datasets-imgs-bottom-btns-batch-wrap">
            <div className="my-dataset-select-all-btn" onClick={onSelectAllClick}>
                {!isSelectAllClicked && <ColorImg src={Images.selectAll}></ColorImg>}
                <span>{isSelectAllClicked ? "取消全选" : "全选"}</span>
            </div>
            <div className="my-dataset-select-count">
                <span>已选中</span>
                <span>{batchId.size}</span>
            </div>
            <div className="my-dataset-datasets-imgs-delete-btn" onClick={onDeleteImgClick}>
                <img src={Images.deleteIcon1}></img>
                <span>删除</span>
            </div>
        </div>;
    };

    const onManualTagClick = (index: number): void => {
        manualTagDiagStartIndex.current = index;
        setIsManualTagDialogVisible(true);
    };

    const onDatasetImgDeleteClick = (imgId: string): void => {
        msgbox("是否删除已选图片", MSG_TYPE.INFO, () => {
            commonStore.runWithLoadingAnim(datasetStore.deleteImgs([imgId]));
        });
    };

    // 数据集图片
    const datasetImgs = (): React.ReactElement => {
        return <div className="my-dataset-datasets-imgs">
            <div className="my-dataset-datasets-imgs-wrap no-scrollbar">
                {datasetStore.curDatasetInfo?.imgs.map((item, i) =>
                    <DatasetImgItem key={i} img={item.url} width={item.width} height={item.height} isChecked={batchId.has(item.id)} isChecking={isBatch} onCheckedChange={a => onBatch(item.id, a)} text={item.text}
                        onManualTagClick={() => onManualTagClick(i)} onDeleteClick={() => onDatasetImgDeleteClick(item.id)}></DatasetImgItem>)}
            </div>
            {isBatch ? datasetsImgsBottomBtnsBatch() : datasetsImgsBottomBtns()}
        </div>;
    };

    const content = (): React.ReactElement => {
        if(!commonStore.getIsLogin() || datasetStore.datasets.length <= 0){
            return empty();
        }
        if(datasetStore.curDatasetInfo === null){
            return datasets();
        }
        if(datasetStore.curDatasetInfo.imgs.length <= 0){
            return uploadImgHint();
        }
        return datasetImgs();
    };

    const onDocumentClick = (): void => {
        setCurSelect("");
    };

    const onManualTagDialogClose = (): void => {
        setIsManualTagDialogVisible(false);
        commonStore.runWithLoadingAnim(datasetStore.updateDatasetImgTexts());
    };

    // 预处理进度条
    const preprocessProgressScroll = (): React.ReactElement => {
        const p = `${Math.floor(progress * 100)}%`;
        return <div className="my-dataset-preprocess-progress" style={{"--progress" : p} as React.CSSProperties}>
            <div className="my-dataset-preprocess-progress-content">
                <div className="my-dataset-preprocess-progress-frame">
                    <div className="my-dataset-preprocess-progress-bar"></div>
                </div>
                <div className="my-dataset-preprocess-progress-hint">{`Loading... ${p}`}</div>
            </div>
        </div>;
    };

    const isImgCountVisible = !!datasetStore.curDatasetInfo && datasetStore.curDatasetInfo.imgs.length > 0;
    return <div className="my-dataset" ref={selfRef}>
        <div className="my-dataset-top-wrap">
            <Breadcrumb itemRender={itemRender} items={pathState} separator={separator}></Breadcrumb>
            {isImgCountVisible && <>
                <span className="my-dataset-img-count">{`${datasetStore.curDatasetInfo.imgs.length}/100`}</span>
                <div className="my-dataset-top-btns-wrap no-scrollbar">
                    {topBtnInfo.map((item, i) =>
                        item.isVisible() && <div data-active={item.isActive()} key={i} className="my-dataset-top-btn" onClick={item.onClick}>
                            <ColorImg src={item.img}></ColorImg>
                            <span>{item.title}</span>
                        </div>)}
                </div>
            </>}
        </div>
        {content()}
        {isManualTagDialogVisible && <ManualTagDialog startIndex={manualTagDiagStartIndex.current} onClose={onManualTagDialogClose}></ManualTagDialog>}
        {progress >= 0 && preprocessProgressScroll()}
        {createNewDatasetDialogState.isVisible && <CreateNewDatasetDialog onClose={onCreateDatasteClose} status={createNewDatasetDialogState}></CreateNewDatasetDialog>}
        <OnClickDocument refObj={selfRef} onClick={onDocumentClick}></OnClickDocument>
    </div>;
}));